import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { CirclePlus } from "lucide-react";
import { useEffect, useState } from "react";
import { LoaderSpin } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { generateTimeEntryMirror } from "@/Api/timeEntryMirror";
import { getSelectUnities } from "@/Api/unities";
import { getSelectEmployees } from "@/Api/employees";
import { useToastNavigate } from "@/utils/toastNavigate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const schema = z.object({
  date_start: z.string().min(1, { message: "*requerido" }),
  date_end: z.string().min(1, { message: "*requerido" }),
  unity_id: z.number().optional(),
  employee_id: z.number().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }: { updateFront: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unities, setUnities] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [formState, setFormState] = useState<string>("default");
  const { toast } = useToastNavigate();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      date_start: "",
      date_end: "",
      unity_id: undefined,
      employee_id: undefined,
    },
  });

  const selectedUnityId = form.watch("unity_id");

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const { data } = await getSelectUnities();
      setUnities(data?.response || []);
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!selectedUnityId) {
      setEmployees([]);
      form.setValue("employee_id", undefined);
      return;
    }
    (async () => {
      const { data } = await getSelectEmployees({
        unity_id: String(selectedUnityId),
        is_active: true,
      });
      setEmployees(data?.response || []);
    })();
  }, [selectedUnityId]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const { data: apiResponse, ok } = await generateTimeEntryMirror(data);

    if (!ok) {
      setFormState("error");
      toast({ description: apiResponse?.message || "Erro ao gerar relatório" });
      setTimeout(() => setFormState("default"), 1000);
      return;
    }

    setFormState("default");
    setIsOpen(false);
    form.reset();
    toast({ description: "Relatório solicitado com sucesso! Acompanhe o status na lista." });
    updateFront();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button variant="outline" className={cn("py-3 flex gap-1", `bg-primary`)}>
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Gerar Relatório
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogDescription className="text-lg">Espelho de Ponto</DialogDescription>
          <DialogTitle className="text-2xl">Gerar Relatório</DialogTitle>
        </DialogHeader>

        <Form onSubmit={form.handleSubmit(formSubmit)} {...form} className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <Form.Row>
              <Form.Field name="date_start">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      A partir de <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input type="date" {...field} />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="date_end">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Até <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input type="date" {...field} />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          </div>

          <Form.Row>
            <Form.Field name="unity_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Unidade de negócio</Form.Label>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "all" ? undefined : Number(value))
                    }
                    value={field.value?.toString() || "all"}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Escolha uma unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as unidades</SelectItem>
                      {unities.map((unity) => (
                        <SelectItem key={unity.id} value={unity.id.toString()}>
                          {unity.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="employee_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Colaborador</Form.Label>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "all" ? undefined : Number(value))
                    }
                    value={field.value?.toString() || "all"}
                    disabled={!selectedUnityId}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          selectedUnityId
                            ? "Todos os colaboradores da unidade"
                            : "Escolha primeiro uma unidade"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os colaboradores</SelectItem>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id.toString()}>
                          {employee.user?.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Gerando relatório <LoaderSpin />
                </>
              )}
              {formState === "default" && "Gerar"}
              {formState === "error" && "Erro ao gerar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
